import { useState } from 'react';

/**
 * SupportFaq — the preview page's "Need support?" + "FAQ" accordions.
 *
 * Static, global content (not per-product — no metafield source), hardcoded
 * from the template-woo preview page. Edit the constants below to change copy.
 * Contact details use the store's real footer info rather than woo's
 * placeholder 555 number.
 */

const SUPPORT_EMAIL = 'support@beyondframesstudio.com';
const SUPPORT_PHONE = '[Phone]';
const SUPPORT_INTRO =
  'Our support team is available 24/7 to help you with your order, technical issues, or any questions about our artwork.';

export function SupportFaq() {
  return (
    <div className="flex flex-col gap-4">
      <Accordion title="Need support?" subtitle="We're always here for you!">
        <p className="text-sm text-warm-brown/90 leading-relaxed m-0">{SUPPORT_INTRO}</p>
        <div className="mt-3 flex flex-col gap-2 text-sm text-warm-brown">
          <a href={`mailto:${SUPPORT_EMAIL}`} className="inline-flex items-center gap-2 hover:text-teal">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="text-teal">
              <rect x="2" y="4" width="20" height="16" rx="2" />
              <path d="m22 7-8.991 5.727a2 2 0 0 1-2.009 0L2 7" />
            </svg>
            {SUPPORT_EMAIL}
          </a>
          <a href={`tel:${SUPPORT_PHONE.replace(/[^+\d]/g, '')}`} className="inline-flex items-center gap-2 hover:text-teal">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="text-teal">
              <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z" />
            </svg>
            {SUPPORT_PHONE}
          </a>
        </div>
      </Accordion>
    </div>
  );
}

interface AccordionProps {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}

function Accordion({ title, subtitle, children }: AccordionProps) {
  const [open, setOpen] = useState(false);
  return (
    <section className="rounded-[18px] bg-white border border-soft-border shadow-soft overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="w-full flex items-center justify-between gap-3 px-5 sm:px-6 py-4 text-left"
      >
        <span className="flex flex-col">
          <span className="text-[13px] font-semibold tracking-[0.12em] uppercase text-warm-brown">{title}</span>
          {subtitle && <span className="text-xs text-warm-brown/60 mt-0.5 normal-case tracking-normal">{subtitle}</span>}
        </span>
        <svg
          width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
          strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
          className={['text-warm-brown/70 transition-transform flex-shrink-0', open ? 'rotate-180' : ''].join(' ')}
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>
      {open && <div className="px-5 sm:px-6 pb-6">{children}</div>}
    </section>
  );
}
